import React, { useEffect, useState } from "react";
import { BellRing, Check, Send } from "lucide-react";
import { api } from "../console/api";
import { Groupe } from "../espace/ui";

const SUJETS = [
  { id: "inscriptions", label: "Inscriptions et désinscriptions", aide: "Cours, essais, stages, workshops, auditions, désinscriptions, places de spectacle" },
  { id: "messages", label: "Messages et absences", aide: "Formulaire de contact du site, absences annoncées par les familles" },
];

const enOctets = (b64) => {
  const brut = atob((b64 + "=".repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...brut].map((c) => c.charCodeAt(0)));
};
const installee = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const possible = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

/* Le téléphone sonne quand une inscription ou un message arrive, comme pour un SMS. */
export const Notifications = () => {
  const [abo, setAbo] = useState(null);
  const [sujets, setSujets] = useState(["inscriptions", "messages"]);
  const [etat, setEtat] = useState("");
  const [occupe, setOccupe] = useState(false);

  useEffect(() => {
    if (!possible()) return;
    navigator.serviceWorker.ready.then((r) => r.pushManager.getSubscription()).then((s) => {
      setAbo(s);
      // Les interrupteurs reprennent les choix déjà enregistrés pour ce téléphone.
      if (s) api("/mobile/push/etat", { method: "POST", body: JSON.stringify({ endpoint: s.endpoint }) })
        .then((e) => setSujets(e.sujets)).catch(() => {});
    }).catch(() => {});
  }, []);

  const enregistrer = async (s, liste) => api("/mobile/push/abonner", {
    method: "POST", body: JSON.stringify({ ...s.toJSON(), sujets: liste }),
  });

  const activer = async () => {
    setEtat(""); setOccupe(true);
    try {
      // La demande d'autorisation doit partir au tap même (règle de l'iPhone).
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setEtat("Autorisation refusée. Réglages du téléphone → Notifications → 1000 ARTS → Autoriser.");
        setOccupe(false); return;
      }
      const { cle } = await api("/mobile/push/cle");
      const reg = await navigator.serviceWorker.ready;
      const s = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: enOctets(cle) });
      await enregistrer(s, sujets);
      setAbo(s); setEtat("C'est activé sur ce téléphone.");
    } catch (e) { setEtat(e.message || "Impossible d'activer les notifications."); }
    setOccupe(false);
  };
  const desactiver = async () => {
    setOccupe(true);
    try {
      await api("/mobile/push/desabonner", { method: "POST", body: JSON.stringify({ endpoint: abo.endpoint }) });
      await abo.unsubscribe();
      setAbo(null); setEtat("Notifications coupées sur ce téléphone.");
    } catch (e) { setEtat(e.message); }
    setOccupe(false);
  };
  const basculer = async (id) => {
    const liste = sujets.includes(id) ? sujets.filter((x) => x !== id) : [...sujets, id];
    setSujets(liste);
    if (abo) await enregistrer(abo, liste).catch(() => {});
  };
  const essayer = async () => {
    setEtat("");
    try { await api("/mobile/push/essai", { method: "POST", body: JSON.stringify({ endpoint: abo.endpoint }) }); setEtat("Envoyée : elle arrive dans quelques secondes."); }
    catch (e) { setEtat(e.message); }
  };

  if (!possible() || (!installee() && /iPhone|iPad/.test(navigator.userAgent))) {
    return (
      <Groupe>
        <div className="p-4 space-y-2 text-[14.5px]">
          <p className="font-semibold">D'abord, installer l'application</p>
          <p className="text-[var(--muted)]">Sur iPhone, les notifications ne marchent qu'avec l'app sur l'écran d'accueil :
            dans Safari, touche <strong>Partager</strong> puis <strong>Sur l'écran d'accueil</strong>, ouvre l'app depuis l'icône,
            et reviens ici.</p>
        </div>
      </Groupe>
    );
  }

  return (
    <div className="space-y-5" data-testid="ecole-notifications">
      <Groupe>
        <div className="p-4 flex items-start gap-3">
          <span className="w-10 h-10 rounded-xl grid place-items-center text-white shrink-0" style={{ background: abo ? "#16A34A" : "#94A3B8" }}>
            <BellRing size={19} />
          </span>
          <div className="flex-1">
            <div className="text-[15px] font-semibold">{abo ? "Activées sur ce téléphone" : "Désactivées sur ce téléphone"}</div>
            <div className="text-[12.5px] text-[var(--muted)] mt-0.5">Le téléphone sonne quand quelque chose arrive, même app fermée.</div>
          </div>
        </div>
        {SUJETS.map((s) => (
          <button key={s.id} onClick={() => basculer(s.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left">
            <span className="flex-1">
              <span className="block text-[15px]">{s.label}</span>
              <span className="block text-[12.5px] text-[var(--muted)]">{s.aide}</span>
            </span>
            <span className="w-[51px] h-[31px] rounded-full p-[2px] transition-colors" style={{ background: sujets.includes(s.id) ? "#34C759" : "#E2E8F0" }}>
              <span className="block w-[27px] h-[27px] rounded-full bg-white shadow transition-transform"
                style={{ transform: sujets.includes(s.id) ? "translateX(20px)" : "none" }} />
            </span>
          </button>
        ))}
      </Groupe>
      {abo ? (
        <div className="flex gap-2">
          <button className="btn-ghost flex-1 justify-center" onClick={essayer} data-testid="push-essai"><Send size={14} /> M'envoyer un essai</button>
          <button className="btn-ghost flex-1 justify-center" onClick={desactiver} disabled={occupe}>Couper</button>
        </div>
      ) : (
        <button className="btn-primary w-full justify-center" onClick={activer} disabled={occupe} data-testid="push-activer">
          <Check size={15} /> Activer les notifications
        </button>
      )}
      {etat && <p className="text-[13px] text-[var(--muted)] px-1">{etat}</p>}
    </div>
  );
};
